import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Pencil, CheckCircle, AlertCircle, Layers, Info } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface Phase {
  id: string;
  name: string;
  order: number;
  isDefault: boolean;
}

interface PhaseManagementProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  currentUser: any;
}

const DEFAULT_PHASES: Phase[] = [
  { id: 'phase_1', name: 'Discovery & Requirements', order: 1, isDefault: true },
  { id: 'phase_2', name: 'Design & Configuration', order: 2, isDefault: true },
  { id: 'phase_3', name: 'Implementation & Testing', order: 3, isDefault: true },
  { id: 'phase_4', name: 'Training & Deployment', order: 4, isDefault: true },
  { id: 'phase_5', name: 'Support & Optimization', order: 5, isDefault: true },
];

export function PhaseManagement({
  accessToken,
  companyId,
  companyName,
  currentUser,
}: PhaseManagementProps) {
  const [phases, setPhases] = useState<Phase[]>(DEFAULT_PHASES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');

  useEffect(() => {
    loadPhases();
  }, [companyId]);

  const loadPhases = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/phases`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.phases && data.phases.length > 0) {
          setPhases(data.phases);
        } else {
          // Use default phases
          setPhases(DEFAULT_PHASES);
        }
      } else {
        console.error('Failed to load phases');
        setPhases(DEFAULT_PHASES);
      }
    } catch (err) {
      console.error('Error loading phases:', err);
      setPhases(DEFAULT_PHASES);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhase = (phase: Phase) => {
    setEditingPhase(phase);
    setNewPhaseName(phase.name);
    setShowEditDialog(true);
    setError('');
    setSuccess(false);
  };

  const handleSavePhase = async () => {
    if (!editingPhase) return;

    if (!newPhaseName.trim()) {
      setError('Phase name cannot be empty');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Update phase name in the list
      const updatedPhases = phases.map(p =>
        p.id === editingPhase.id
          ? { ...p, name: newPhaseName.trim(), isDefault: false }
          : p
      );

      // Save to backend
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/phases`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ phases: updatedPhases }),
        }
      );

      if (response.ok) {
        setPhases(updatedPhases);
        setSuccess(true);
        setShowEditDialog(false);
        setEditingPhase(null);
        setNewPhaseName('');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save phase');
      }
    } catch (err: any) {
      console.error('Error saving phase:', err);
      setError('Failed to save phase');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!confirm('Reset all phases to default names? This will replace any custom phase names.')) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/phases`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ phases: DEFAULT_PHASES }),
        }
      );

      if (response.ok) {
        setPhases(DEFAULT_PHASES);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to reset phases');
      }
    } catch (err: any) {
      console.error('Error resetting phases:', err);
      setError('Failed to reset phases');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = 
    currentUser.role === 'consultant' ||
    currentUser.role === 'cfAdmin' ||
    currentUser.role === 'systemAdmin';

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading phases...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            Phase names updated.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          Customize implementation phase names to match your firm's methodology. Default phases can be renamed to align with client expectations and terminology.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Implementation Phases
              </CardTitle>
              <CardDescription>
                Customize phase names for {companyName}
              </CardDescription>
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetToDefault}
                disabled={saving}
              >
                Reset to Default
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {phases.map((phase) => (
              <div
                key={phase.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold">
                    {phase.order}
                  </div>
                  <div>
                    <p className="font-medium">{phase.name}</p>
                    {!phase.isDefault && (
                      <p className="text-xs text-muted-foreground">Custom name</p>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditPhase(phase)}
                    disabled={saving}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Rename
                  </Button>
                )}
              </div>
            ))}
          </div>

          {!canEdit && (
            <p className="text-sm text-muted-foreground mt-4">
              Only consultants and firm administrators can customize phase names.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit Phase Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Phase</DialogTitle>
            <DialogDescription>
              Customize the phase name for this implementation
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phase-name">Phase Name</Label>
              <Input
                id="phase-name"
                value={newPhaseName}
                onChange={(e) => setNewPhaseName(e.target.value)}
                placeholder="Enter phase name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePhase();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Original: {editingPhase?.isDefault ? editingPhase.name : 'Custom phase'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setEditingPhase(null);
                setNewPhaseName('');
                setError('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePhase} disabled={saving || !newPhaseName.trim()}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}